import { useState, type ChangeEvent, type FocusEvent, type SubmitEvent } from "react";
import { useLocation } from "react-router";

import { notFoundPath, postForm, unavailableMessage, type FieldErrors } from "../api/client.js";
import { rememberAuthHint } from "../app/auth-hint.js";
import { isAppPage } from "../app/navigation.js";
import { loginUrlAfterUnauthorized } from "../app/session-expiry.js";
import { useGo } from "../app/use-go.js";
import type { MoveState } from "../components/notice.js";

import { focusField, formValueOf, useFieldCheck } from "./use-field-check.js";
import type { FormRules, ValueOf } from "./validation.js";

/** フォームの状態と、画面に渡すもの。 */
export interface ApiForm {
    /** 送っている間。ボタンを押せなくする（確認コードは 1 回で使い切る）。 */
    readonly busy: boolean;
    /** 欄に結び付かない断り。フォームの先頭に出す。 */
    readonly summary: readonly string[];
    /** その欄の断り。無ければ undefined。 */
    readonly fieldError: (name: string) => string | undefined;
    readonly onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
    /** 入力のたび（フォームに付ける）。送る前に断った欄を見直す。 */
    readonly onChange: (event: ChangeEvent<HTMLFormElement>) => void;
    /** 欄を離れたとき（フォームに付ける）。 */
    readonly onBlur: (event: FocusEvent<HTMLFormElement>) => void;
}

const noRules: FormRules = {};

/** 欄の名前。<b>送ったフォームに無い名前の断りは、先頭の枠へ回す</b>——どこにも出ないまま消さない。 */
const fieldNamesOf = (form: HTMLFormElement): ReadonlySet<string> => {
    const names = new Set<string>();
    for (const element of form.elements) {
        // 複数行の欄（textarea）も欄として数える。数えないと断りが欄の下でなく先頭の枠に出る
        const field = (element instanceof HTMLInputElement && element.type !== "hidden") || element instanceof HTMLTextAreaElement;
        if (field && element.name !== "") {
            names.add(element.name);
        }
    }
    return names;
};

const bodyOf = (form: HTMLFormElement): URLSearchParams => {
    const body = new URLSearchParams();
    const fields = new FormData(form);
    for (const [name, value] of fields) {
        if (typeof value === "string") {
            body.append(name, value);
        }
    }
    return body;
};

/**
 * フォームを API へ送る（docs/02 §9）。
 *
 * <b>フォームを欄の名前ごとそのまま送る。</b>API はフォームを受け取る形にしてあり、
 * 検証もチェックボックスの扱いも、API の ViewModel が決める。
 * <b>戻り先（?returnUrl=）はクエリのまま API へ渡す。</b>確かめるのはサーバ。
 *
 * <c>rules</c> を渡すと、<b>送る前に</b>その規則で見る（ViewModel の写し。<c>view-model-rules.ts</c>）。
 * 断った欄があれば送らず、欄の下に出す。<b>送った後の断り（API）はこれまでどおり</b>出す。
 * 規則を渡す画面は、フォームに <c>onChange</c> と <c>onBlur</c> も付ける。
 *
 * <c>dialog</c> を渡すと、<b>送る直前に</b>その文でブラウザの確認ダイアログを出す（重要な操作。docs/01 §7）。
 * 規則で断ったときは聞かない。キャンセルなら何も送らない。欄の値を文に入れるときは関数で渡す。
 */
export const useApiForm = (
    action: string,
    rules: FormRules = noRules,
    dialog?: string | ((valueOf: ValueOf) => string),
): ApiForm => {
    const { pathname, search } = useLocation();
    const move = useGo();
    const check = useFieldCheck(rules);

    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [fields, setFields] = useState<ReadonlySet<string>>(new Set());

    const go = async (location: string, state?: MoveState): Promise<void> => {
        // ログイン・ログアウトの後はヘッダーを直してもらう（ClientApp/session/header-session.ts）。
        // React の画面の中で移るとページを読み込み直さないため
        document.dispatchEvent(new CustomEvent("portal:session"));
        // アカウントの操作（ログイン・二段階認証・確認コード・ログアウト・退会など）の後にページごと移るなら、
        // 移る前に状態を控える。移った先のヘッダーが、管理センターの入口を最初から出せる（app/auth-hint.ts）
        if (action.startsWith("/api/account/") && !isAppPage(location)) {
            await rememberAuthHint();
        }
        move(location, state);
    };

    const submit = async (form: HTMLFormElement): Promise<void> => {
        setBusy(true);
        setErrors({});
        setFields(fieldNamesOf(form));

        try {
            const outcome = await postForm(`${action}${search}`, bodyOf(form));
            switch (outcome.kind) {
                case "go": {
                    await go(outcome.location, { notice: outcome.notice, detail: outcome.detail });
                    return;
                }
                case "unauthorized": {
                    // ログインの途中（確認コードなど）が切れたなら最初から。
                    // ログインしていた画面で送ったなら、切れたことを知らせてこの画面へ戻れるようにする
                    await go(pathname.startsWith("/Account/")
                        ? "/Account/Login"
                        : loginUrlAfterUnauthorized(pathname + search, true));
                    return;
                }
                case "notFound": {
                    await go(notFoundPath);
                    return;
                }
                case "errors": {
                    setErrors(outcome.errors);
                    break;
                }
            }
        } catch {
            setErrors({ "": [unavailableMessage] });
        }

        setBusy(false);
    };

    return {
        busy,
        summary: Object.entries(errors)
            .filter(([name]) => !fields.has(name))
            .flatMap(([, messages]) => messages),
        // 送る前の断りを先に出す（直した欄の API の断りは、次に送るまで残る）
        fieldError: (name) => check.error(name) ?? (fields.has(name) ? errors[name]?.join(" ") : undefined),
        onSubmit: (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const invalid = check.checkAll(formValueOf(form));
            if (invalid !== undefined) {
                // 送らない。前に送った断りは今の入力のものではないので消す
                setErrors({});
                focusField(form, invalid);
                return;
            }
            const question = typeof dialog === "function" ? dialog(formValueOf(form)) : dialog;
            if (question !== undefined && !globalThis.confirm(question)) {
                return;
            }
            void submit(form);
        },
        onChange: (event) => {
            check.recheck(formValueOf(event.currentTarget));
        },
        onBlur: (event) => {
            const name = event.target.getAttribute("name");
            if (name !== null) {
                check.leave(name, formValueOf(event.currentTarget));
            }
        },
    };
};
